<template>
    <BaseSelect
        v-if="cityOptions"
        v-model="selectedValue"
        :options="cityOptions"
        @input="updateFilterState"
    >
        <template #default>
            <option
                :value="null"
                :selected="selectedValue === ''"
            >
                Overal
            </option>

            <optgroup label="Regio">
                <option
                    v-for="option in regionOptions"
                    :key="option.value"
                    :value="`${REGION_FILTER}-${option.value}`"
                    :selected="`${REGION_FILTER}-${option.value}` === selectedValue"
                >
                    {{ option.label }}
                </option>
            </optgroup>

            <optgroup label="Stad">
                <option
                    v-for="option in cityOptions"
                    :key="option.value"
                    :value="`${CITY_FILTER}-${option.value}`"
                    :selected="`${CITY_FILTER}-${option.value}` === selectedValue"
                >
                    {{ option.label }}
                </option>
            </optgroup>
        </template>

        <template #pre>
            <BaseIcon icon="location" />
        </template>
    </BaseSelect>
</template>

<script setup lang="ts">
import type { CityFilterOptions, RegionFilterOptions } from '~/types/data';

const CITY_FILTER = 'city';
const REGION_FILTER = 'region';
const LOCAL_STORAGE_KEY = `filter-${CITY_FILTER}-${REGION_FILTER}`;

const { filterOptions, filtersVisible, cityOptions, regionOptions, mainProgramEntry } = storeToRefs(useEventFiltersStore());

const route = useRoute();

const getSelectedValue = () => {
    if (CITY_FILTER in route.query) {
        return `${CITY_FILTER}-${route.query.city}`;
    }

    if (REGION_FILTER in route.query) {
        return `${REGION_FILTER}-${route.query.region}`;
    }

    return null;
};

const selectedValue = ref(getSelectedValue());

const filterOptionsResponse = await useAsyncFetchLaravel<{
    region: RegionFilterOptions;
    city: CityFilterOptions;
}>('/v2.0/events/filters');

filterOptions.value = filterOptionsResponse.data.value;

watch(() => route.query.city, (value) => {
    if (value) {
        selectedValue.value = `${CITY_FILTER}-${value}`;
    } else if (!route.query.region) {
        selectedValue.value = null;
    }
});

watch(() => route.query?.region, (value) => {
    if (value) {
        selectedValue.value = `${REGION_FILTER}-${value}`;
    } else if (!route.query.city) {
        selectedValue.value = null;
    }
});

onBeforeMount(async() => {
    if (
        !selectedValue.value &&
        filtersVisible.value &&
        localStorage.getItem(LOCAL_STORAGE_KEY)
    ) {
        const storageFilter = localStorage.getItem(LOCAL_STORAGE_KEY);

        selectedValue.value = storageFilter;

        await updateFilterState(selectedValue.value);
    }
});

const updateFilterState = async(value: string|null) => {
    const newRoute = {
        path: route.path,
        query: { ...route.query }
    };

    if (!value) {
        delete newRoute.query.city;
        delete newRoute.query.region;

        localStorage.removeItem(LOCAL_STORAGE_KEY);
    } else {
        const selected = value.split('-');

        if (selected[0] === REGION_FILTER) {
            newRoute.query.region = selected[1];

            delete newRoute.query.city;
        }

        if (selected[0] === CITY_FILTER) {
            newRoute.query.city = selected[1];

            delete newRoute.query.region;
        }

        localStorage.setItem(LOCAL_STORAGE_KEY, value);
    }

    // If homepage, redirect to main programme entry containing filters
    if (route.name === 'index' && mainProgramEntry.value) {
        newRoute.path = mainProgramEntry.value.uri as string;
    }

    await navigateTo(newRoute);

    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    });
};
</script>

<style lang="less" src="./FilterCitySelect.less" />
